/*
 * @author        zach.william@zumper.com (Zach William)
 * @copyright     Copyright (c) 2023, Zumper
 * @description   BackNavButton.tsx
 */

import type { LinkProps } from '@chakra-ui/react'
import { BackNavButton } from '@zcode-ui/web-zumper-features'
import { trackGAEvent } from '@zcode/nr-utils'
import type { State } from '@zcode/state-type'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useDetailPageContext } from '../../DetailPageContext'
import { useDetailMixpanelClick } from '../../hooks/useDetailMixpanelClick'

const selectCityUrl = (state: State) => {
  const entity = state.detail.entity.data
  // Entity is loaded when this component renders
  if (!entity?.cityUrl) {
    return ''
  }
  return entity.cityUrl
}

const selectCity = (state: State) => {
  const entity = state.detail.entity.data
  // Entity is loaded when this component renders
  if (!entity?.city) {
    return ''
  }
  return entity.city
}

export const DetailBackNavButton = ({ className }: { className?: string }) => {
  const trackDetailMixpanelClick = useDetailMixpanelClick('BACK')
  const city = useSelector(selectCity)
  const cityUrl = useSelector(selectCityUrl)
  const {
    getRootRelativeUrlFromFiltersAndLocation,
    keepBetaParam,
    preloadRoutes,
    Routes,
    AnalyticsConsts,
    handleBackBtnClick,
  } = useDetailPageContext()

  const onClick = useCallback(() => {
    trackGAEvent(AnalyticsConsts.FULL_DETAIL, 'Back to list')
    trackDetailMixpanelClick()
  }, [AnalyticsConsts, trackDetailMixpanelClick])

  return (
    <BackNavButton
      Routes={Routes}
      city={city}
      cityUrl={cityUrl}
      className={className}
      getRootRelativeUrlFromFiltersAndLocation={getRootRelativeUrlFromFiltersAndLocation}
      handleBackBtnClick={handleBackBtnClick}
      keepBetaParam={keepBetaParam}
      onClick={onClick}
      preloadRoutes={preloadRoutes}
    />
  )
}
